import { useState } from 'react'

import { PlanFrequency } from '../../../constants'
import { ComponentType } from '../../../types'
import { ChooseFrequencyLaterSteps_OfferLockDataFragment } from '../__generated__/choose-frequency-later-steps.graphql'
import {
  ChooseFrequencyLaterModalSteps,
  ChooseFrequencyLaterModalStepsType,
  ChooseFrequencyLaterPlanOffer,
} from '../types'
import { ChooseFrequencyLaterContext } from './choose-frequency-later-context'

export interface ChooseFrequencyLaterContextProviderProps {
  children: React.ReactNode
  offerLockData: ChooseFrequencyLaterSteps_OfferLockDataFragment
  componentType: ComponentType
}

function getOffersPerRecurrence(
  offerTitle = '',
  allCards: readonly ChooseFrequencyLaterPlanOffer[] = []
) {
  const allFrequenciesPlanOffers = allCards.filter(
    (card) => card?.title?.toLowerCase() === offerTitle.toLowerCase()
  )

  const yearlyOffer = allFrequenciesPlanOffers.find(
    (card) => card?.offer?.frequency === PlanFrequency.YEARLY
  )
  const monthlyOffer = allFrequenciesPlanOffers.find(
    (card) => card?.offer?.frequency === PlanFrequency.MONTHLY
  )

  return { yearlyOffer, monthlyOffer }
}

function buildPlanOffers({
  monthlyOffer,
  yearlyOffer,
}: {
  monthlyOffer?: ChooseFrequencyLaterPlanOffer
  yearlyOffer?: ChooseFrequencyLaterPlanOffer
}) {
  return [monthlyOffer, yearlyOffer].filter(Boolean) as ChooseFrequencyLaterPlanOffer[]
}

export function ChooseFrequencyLaterContextProvider({
  children,
  offerLockData,
  componentType,
}: ChooseFrequencyLaterContextProviderProps) {
  const isPaywall = componentType === 'paywall'
  const hasYearlyOffers = !!offerLockData?.header?.toggle?.options?.includes?.('Anual')
  const hasMonthlyOffers = !!offerLockData?.header?.toggle?.options?.includes?.('Mensal')
  const defaultFrequency =
    offerLockData.header?.toggle?.defaultOption === 'Anual'
      ? PlanFrequency.YEARLY
      : PlanFrequency.MONTHLY

  const [currentStep, setCurrentStep] = useState<ChooseFrequencyLaterModalStepsType>(
    ChooseFrequencyLaterModalSteps.SELECT_OFFER
  )
  const [selectedPlanOffers, setSelectedPlanOffers] = useState<ChooseFrequencyLaterPlanOffer[]>([])
  const [selectedFrequencyOffer, setSelectedFrequencyOffer] =
    useState<ChooseFrequencyLaterPlanOffer | null>(null)

  const handleSelectFrequencyOffer = (selectedOffer: ChooseFrequencyLaterPlanOffer | null) => {
    setSelectedFrequencyOffer(selectedOffer)
  }

  const handleSelectPlan = (offerTitle: string) => {
    const { yearlyOffer, monthlyOffer } = getOffersPerRecurrence(
      offerTitle,
      offerLockData.body.cards
    )

    const planOffers = buildPlanOffers({ monthlyOffer, yearlyOffer })

    if (planOffers.length === 0) return

    const frequencyToFilter = selectedFrequencyOffer?.offer?.frequency || defaultFrequency
    const selectedOffer = planOffers.find((o) => o.offer.frequency === frequencyToFilter) ?? null

    setSelectedPlanOffers(planOffers)
    handleSelectFrequencyOffer(selectedOffer)
    setCurrentStep(ChooseFrequencyLaterModalSteps.SELECT_FREQUENCY)
  }

  const backToSelectOffer = () => {
    setSelectedPlanOffers([])
    handleSelectFrequencyOffer(null)
    setCurrentStep(ChooseFrequencyLaterModalSteps.SELECT_OFFER)
  }

  const values = {
    isPaywall,
    hasYearlyOffers,
    hasMonthlyOffers,
    currentStep,
    selectedPlanOffers,
    selectedFrequencyOffer,
    handleSelectPlan,
    handleSelectFrequencyOffer,
    backToSelectOffer,
  }

  return (
    <ChooseFrequencyLaterContext.Provider value={values}>
      {children}
    </ChooseFrequencyLaterContext.Provider>
  )
}
